import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { FiSearch, FiTool } from "react-icons/fi";
import GatedButton from "../GatedButton";
import { LoadingSpinner } from "../LoadingSpinner";
import {
	CheckDescriptorDto,
	CheckRunDto,
	extractErrorMessage,
	formatBytes,
	formatDateTimeFr
} from "./checksCommon";

/* ------------------------------------------------------------------ */
/*  Modèle local                                                       */
/* ------------------------------------------------------------------ */

/** Résultat normalisé d'une analyse ou d'une correction, quel que soit le contrôle. */
type RunOutcome = {
	errorCount: number;
	warningCount: number;
	summary: string;
	details: string[];
};

type CardState = {
	running: boolean;
	fixing: boolean;
	error: string | null;
	notice: string | null;
	outcome: RunOutcome | null;
};

const EMPTY_CARD: CardState = { running: false, fixing: false, error: null, notice: null, outcome: null };

const CFG = { withCredentials: true, headers: { Accept: "application/json" } };

/** Jeton de permission du bouton « Corriger » de chaque contrôle : celui que le serveur exige
 * sur le fixEndpoint. Les contrôles absents de la table retombent sur settings:write. */
const FIX_TOKENS: Record<string, string> = {
	integrity: "feature:settings:write",
	"parity-preview": "feature:deployment:write",
	"deployed-files-preview": "feature:deployment:write",
	"deployed-files-production": "feature:deployment:write",
	"search-health": "feature:settings:write"
};

const fixToken = (checkId: string): string => FIX_TOKENS[checkId] ?? "feature:settings:write";

const SCOPE_FR: Record<string, string> = {
	site: "site",
	preview: "preview",
	production: "production"
};

const DETAIL_CAP = 100;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/* ------------------------------------------------------------------ */
/*  Normalisation des rapports serveur → RunOutcome                    */
/* ------------------------------------------------------------------ */

/** IntegrityReport (POST /api/integrity/scan, ou reportAfter du fix). */
const integrityOutcome = (r: any): RunOutcome => {
	const issues = Array.isArray(r?.issues) ? r.issues : [];
	const errors = Number(r?.errorCount ?? 0);
	const warnings = Number(r?.warningCount ?? 0);
	return {
		errorCount: errors,
		warningCount: warnings,
		summary:
			r?.isStable === true
				? `Site stable : aucune erreur (${warnings} avertissement(s)).`
				: `${errors} erreur(s) et ${warnings} avertissement(s) détecté(s).`,
		details: issues.slice(0, DETAIL_CAP).map((i: any) => `${i?.checkTitle ?? i?.checkId ?? "?"} : ${i?.detail ?? ""}`)
	};
};

/** DeployedFileScanResult / DeployedFileRepairResult (scan|repair /api/admin/deployed-files). */
const deployedFilesOutcome = (d: any, repaired: boolean): RunOutcome => {
	const missing = Number(d?.missingCount ?? (Array.isArray(d?.missingInStorage) ? d.missingInStorage.length : 0));
	const extra = Number(d?.extraCount ?? (Array.isArray(d?.extraInStorage) ? d.extraInStorage.length : 0));
	const mismatch = Number(d?.hashMismatchCount ?? (Array.isArray(d?.hashMismatch) ? d.hashMismatch.length : 0));
	const fixed = Number(d?.fixedCount ?? 0);
	const details = [
		...(Array.isArray(d?.missingInStorage) ? d.missingInStorage : []).map((p: any) => `Manquant dans le stockage : ${p}`),
		...(Array.isArray(d?.extraInStorage) ? d.extraInStorage : []).map((p: any) => `En trop dans le stockage : ${p}`),
		...(Array.isArray(d?.hashMismatch) ? d.hashMismatch : []).map((p: any) => `Empreinte différente : ${p}`)
	].slice(0, DETAIL_CAP);
	return {
		errorCount: missing + extra + mismatch,
		warningCount: 0,
		summary:
			`${missing} manquant(s), ${extra} en trop, ${mismatch} empreinte(s) différente(s)` +
			`${repaired ? ` : ${fixed} corrigé(s)` : ""}` +
			` (déployés : ${Number(d?.totalDeployed ?? 0)}, stockage : ${Number(d?.totalStorage ?? 0)}).`,
		details
	};
};

/** Rapport du coordinateur de maintenance (parité SQL ↔ read store), scan comme réparation. */
const parityReportOutcome = (data: any): RunOutcome => {
	const state = String(data?.status?.state ?? "").toLowerCase();
	if (state === "failed") {
		throw new Error(String(data?.status?.error ?? "L'opération a échoué côté serveur."));
	}
	const report = data?.report ?? null;
	if (!report) {
		return { errorCount: 0, warningCount: 0, summary: "Aucun rapport disponible : relancez l'analyse.", details: [] };
	}
	const issues = Array.isArray(report.issues) ? report.issues : [];
	const applied = report.applied === true;
	const failedFixes = issues.filter((i: any) => String(i?.outcome ?? "").toLowerCase().startsWith("failed")).length;
	return {
		errorCount: applied ? failedFixes : issues.length,
		warningCount: 0,
		summary: applied
			? `Réparation appliquée : ${Number(report.redeploys ?? 0)} redéploiement(s), ` +
				`${Number(report.markerFixes ?? 0)} marqueur(s) corrigé(s), ` +
				`${Number(report.orphansRemoved ?? 0)} orphelin(s) supprimé(s)` +
				`${failedFixes ? `, ${failedFixes} correction(s) en échec` : ""}.`
			: issues.length === 0
				? `Conforme à la source : ${Number(report.pagesChecked ?? 0)} page(s) vérifiée(s).`
				: `${issues.length} divergence(s) détectée(s).`,
		details: issues
			.slice(0, DETAIL_CAP)
			.map(
				(i: any) =>
					`${i?.kind ?? "?"} : ${i?.location ?? i?.contentId ?? "?"} : ${i?.detail ?? ""}${i?.outcome ? ` [${i.outcome}]` : ""}`
			)
	};
};

/* ------------------------------------------------------------------ */
/*  Sparkline (SVG inline, sans lib)                                   */
/* ------------------------------------------------------------------ */

/** Tendance des errorCount des derniers runs : l'historique arrive du plus récent au plus
 * ancien, on le retourne pour dessiner chronologiquement. Rouge quand le dernier run a des
 * erreurs, bleu sinon. Rien à dessiner sans historique. */
const Sparkline: React.FC<{ checkId: string; runs: CheckRunDto[] }> = ({ checkId, runs }) => {
	const points = [...runs].reverse().map((r) => Number(r.errorCount ?? 0));
	if (points.length === 0) return null;
	const width = 120;
	const height = 28;
	const pad = 3;
	const max = Math.max(1, ...points);
	const step = points.length > 1 ? (width - 2 * pad) / (points.length - 1) : 0;
	const x = (i: number) => pad + i * step;
	const y = (v: number) => height - pad - (v / max) * (height - 2 * pad);
	const coords = points.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
	const last = points[points.length - 1];
	return (
		<svg
			data-testid={`sparkline-${checkId}`}
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			role="img"
			aria-label={`Tendance des erreurs sur ${points.length} analyse(s)`}
			className={`shrink-0 ${last > 0 ? "text-rose-600" : "text-sky-600"}`}
		>
			{points.length > 1 && (
				<polyline fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" points={coords.join(" ")} />
			)}
			<circle cx={x(points.length - 1)} cy={y(last)} r="2.5" fill="currentColor" />
		</svg>
	);
};

/* ------------------------------------------------------------------ */
/*  Panneau                                                            */
/* ------------------------------------------------------------------ */

/**
 * « Analyse approfondie » : une carte par contrôle du catalogue code-first
 * (GET /api/checks/catalog) : « Analyser » lance le contrôle (appel direct, ou flux 202 + poll
 * du statut pour les parités qui tournent dans le coordinateur de maintenance), la zone
 * résultat affiche les compteurs erreurs/avertissements avec le détail replié, « Corriger »
 * (séparé, gardé par le jeton de permission du fixEndpoint) n'existe que pour les contrôles
 * corrigeables, et la sparkline retrace les errorCount des ~20 derniers runs historisés
 * (GET /api/checks/history). Chaque exécution est historisée côté serveur automatiquement.
 */
const DeepAnalyzePanel: React.FC<{ pollMs?: number; maxPollMs?: number }> = ({
	pollMs = 2500,
	maxPollMs = 20 * 60 * 1000
}) => {
	const [catalog, setCatalog] = useState<CheckDescriptorDto[] | null>(null);
	const [catalogError, setCatalogError] = useState<string | null>(null);
	const [history, setHistory] = useState<Record<string, CheckRunDto[]>>({});
	const [cards, setCards] = useState<Record<string, CardState>>({});
	const mountedRef = useRef(true);

	const updateCard = (id: string, patch: Partial<CardState>) => {
		if (!mountedRef.current) return;
		setCards((prev) => ({ ...prev, [id]: { ...EMPTY_CARD, ...prev[id], ...patch } }));
	};

	const loadHistoryFor = useCallback(async (checkId: string) => {
		try {
			const res = await axios.get(`/api/checks/history?checkId=${encodeURIComponent(checkId)}&limit=20`, CFG);
			if (!mountedRef.current) return;
			setHistory((prev) => ({ ...prev, [checkId]: Array.isArray(res.data) ? res.data : [] }));
		} catch {
			// L'historique est décoratif : son échec ne bloque jamais la carte.
		}
	}, []);

	const loadCatalog = useCallback(async () => {
		setCatalogError(null);
		try {
			const res = await axios.get("/api/checks/catalog", CFG);
			if (!mountedRef.current) return;
			const list = Array.isArray(res.data) ? (res.data as CheckDescriptorDto[]) : [];
			setCatalog(list);
			await Promise.all(list.map((c) => loadHistoryFor(c.id)));
		} catch (err) {
			if (!mountedRef.current) return;
			setCatalogError(extractErrorMessage(err, "Impossible de charger le catalogue des contrôles."));
		}
	}, [loadHistoryFor]);

	useEffect(() => {
		mountedRef.current = true;
		void loadCatalog();
		return () => {
			mountedRef.current = false;
		};
	}, [loadCatalog]);

	/* ---- Flux 202 + poll (coordinateur de maintenance, route de statut partagée) ------------- */

	/** Interroge le statut du coordinateur jusqu'à la fin du travail ; null quand la fenêtre de
	 * suivi est dépassée (le travail continue côté serveur). Les échecs transitoires du statut
	 * ne tuent pas la boucle. */
	const pollRepairStatus = async (): Promise<any | null> => {
		const started = Date.now();
		for (;;) {
			let data: any = null;
			try {
				data = (await axios.get("/api/deployment/preview/repair/status", CFG)).data ?? null;
			} catch {
				// Transitoire : on continue d'interroger.
			}
			if (data) {
				const state = String(data?.status?.state ?? "").toLowerCase();
				const running = data.running === true || state === "running";
				if (!running) return data;
			}
			if (Date.now() - started > maxPollMs) return null;
			await sleep(pollMs);
		}
	};

	/** Interroge le statut du rebuild de l'index jusqu'à Completed/Failed ; « timeout » quand la
	 * fenêtre de suivi est dépassée. */
	const pollSearchRebuild = async (): Promise<"completed" | "timeout"> => {
		const started = Date.now();
		for (;;) {
			let status: any;
			try {
				status = (await axios.get("/api/search/index/rebuild/status", CFG)).data?.status;
			} catch {
				status = undefined;
			}
			const state = String(status?.state ?? "").toLowerCase();
			if (state === "completed") return "completed";
			if (state === "failed") throw new Error(String(status?.error ?? "Échec de la reconstruction de l'index."));
			if (Date.now() - started > maxPollMs) return "timeout";
			await sleep(pollMs);
		}
	};

	/** Lance un travail du coordinateur en tolérant le 409 « déjà en cours » : le coordinateur
	 * ne fait tourner qu'un travail à la fois, on s'attache alors à celui en cours. */
	const startCoordinatorJob = async (url: string, checkId: string) => {
		try {
			await axios.post(url, null, CFG);
		} catch (err: any) {
			if (err?.response?.status !== 409) throw err;
			updateCard(checkId, { notice: "Une opération de maintenance était déjà en cours : suivi du travail en cours." });
		}
	};

	const runParityScan = async (checkId: string, startUrl: string, targetLabel: string): Promise<RunOutcome> => {
		await startCoordinatorJob(startUrl, checkId);
		const data = await pollRepairStatus();
		if (data == null) {
			return {
				errorCount: 0,
				warningCount: 0,
				summary: `L'analyse de la ${targetLabel} continue côté serveur : relancez plus tard pour lire le résultat.`,
				details: []
			};
		}
		return parityReportOutcome(data);
	};

	/* ---- Analyses ----------------------------------------------------------------------------- */

	const searchHealthOutcome = async (): Promise<RunOutcome> => {
		const [preview, production] = await Promise.all([
			axios.get("/api/checks/search-health?target=preview", CFG),
			axios.get("/api/checks/search-health?target=production", CFG)
		]);
		const parts = [preview.data ?? {}, production.data ?? {}];
		const totalDelta = parts.reduce((acc: number, part: any) => acc + Math.abs(Number(part?.delta ?? 0)), 0);
		const lines = parts.map(
			(part: any) =>
				`${part?.target ?? "?"} : ${Number(part?.actual ?? 0)} indexé(s) pour ${Number(part?.expected ?? 0)} attendu(s) (écart ${Number(part?.delta ?? 0)}).`
		);
		return { errorCount: totalDelta, warningCount: 0, summary: lines.join(" "), details: lines };
	};

	const executeRun = async (id: string): Promise<RunOutcome> => {
		switch (id) {
			case "integrity": {
				const r = (await axios.post("/api/integrity/scan", null, CFG)).data ?? {};
				return integrityOutcome(r);
			}
			case "broken-links": {
				const d = (await axios.post("/api/admin/links/scan", null, CFG)).data ?? {};
				const items = Array.isArray(d.items) ? d.items : [];
				const broken = Number(d.brokenCount ?? items.length);
				return {
					errorCount: broken,
					warningCount: 0,
					summary: `${broken} lien(s) cassé(s) sur ${Number(d.totalLinks ?? 0)} analysé(s).`,
					details: items
						.slice(0, DETAIL_CAP)
						.map(
							(it: any) =>
								`${it?.sourceTitle || it?.sourceSlug || it?.sourceContentId || "Source inconnue"} ` +
								`(${it?.sourceContentType ?? "?"}/${it?.sourceLanguage ?? "?"}) : ${it?.reason || "raison inconnue"}` +
								`${it?.linkPath ? ` · ${it.linkPath}` : ""}`
						)
				};
			}
			case "parity-preview":
				return runParityScan(id, "/api/deployment/preview/repair-scan", "preview");
			case "parity-production":
				return runParityScan(id, "/api/deployment/production/integrity-scan", "production");
			case "deployed-files-preview": {
				const d = (await axios.post("/api/admin/deployed-files/scan/preview", null, CFG)).data ?? {};
				return deployedFilesOutcome(d, false);
			}
			case "deployed-files-production": {
				const d = (await axios.post("/api/admin/deployed-files/scan/production", null, CFG)).data ?? {};
				return deployedFilesOutcome(d, false);
			}
			case "search-health":
				return searchHealthOutcome();
			case "redirect-health": {
				const d = (await axios.get("/api/checks/redirect-health", CFG)).data ?? {};
				const loops = Array.isArray(d.loops) ? d.loops : [];
				const openPreview = Number(d.openPreview ?? 0);
				const openProduction = Number(d.openProduction ?? 0);
				return {
					errorCount: loops.length + openProduction,
					warningCount: openPreview,
					summary:
						`${loops.length} boucle(s) de redirection · ${openProduction} tâche(s) ouverte(s) en production · ` +
						`${openPreview} en preview.`,
					details: loops.slice(0, DETAIL_CAP).map((l: any) => `${l?.fromPath ?? "?"} → ${l?.toPath ?? "?"} : ${l?.raison ?? ""}`)
				};
			}
			case "media-orphans": {
				const d = (await axios.post("/api/storage/orphans/scan", null, CFG)).data ?? {};
				const files = Array.isArray(d.files) ? d.files : [];
				const orphans = files
					.filter((f: any) => f?.verdict === "orphan")
					.sort((a: any, b: any) => Number(b?.size ?? 0) - Number(a?.size ?? 0));
				const leaks = Array.isArray(d.pdfExtractedTextLeaks) ? d.pdfExtractedTextLeaks : [];
				return {
					errorCount: leaks.length,
					warningCount: Number(d.orphanCount ?? orphans.length),
					summary:
						`${Number(d.orphanCount ?? 0)} fichier(s) orphelin(s) (${formatBytes(Number(d.orphanBytes ?? 0))}) ` +
						`sur ${Number(d.totalFiles ?? 0)} · ${leaks.length} fuite(s) PdfExtractedTexts. ` +
						`Détail complet dans l'onglet « Médias ».`,
					details: orphans.slice(0, 25).map((f: any) => `${f?.key} (${formatBytes(Number(f?.size ?? 0))})`)
				};
			}
			default:
				throw new Error("Ce contrôle n'est pas encore pris en charge par cette version de l'interface.");
		}
	};

	/* ---- Corrections ---------------------------------------------------------------------------- */

	const executeFix = async (id: string): Promise<RunOutcome> => {
		switch (id) {
			case "integrity": {
				const r = (await axios.post("/api/integrity/fix", { targetIds: null }, CFG)).data ?? {};
				const base = r.reportAfter
					? integrityOutcome(r.reportAfter)
					: { errorCount: 0, warningCount: 0, summary: "", details: [] };
				return {
					...base,
					summary: `${Number(r.fixedIssues ?? 0)} problème(s) corrigé(s)` +
						`${Number(r.failedIssues ?? 0) ? `, ${Number(r.failedIssues)} en échec` : ""}. ${base.summary}`.trimEnd()
				};
			}
			case "parity-preview": {
				await startCoordinatorJob("/api/deployment/preview/repair", id);
				const data = await pollRepairStatus();
				if (data == null) {
					return {
						errorCount: 0,
						warningCount: 0,
						summary: "La réparation continue côté serveur : relancez l'analyse plus tard pour lire le résultat.",
						details: []
					};
				}
				return parityReportOutcome(data);
			}
			case "deployed-files-preview": {
				const d = (await axios.post("/api/admin/deployed-files/repair/preview", null, CFG)).data ?? {};
				return deployedFilesOutcome(d, true);
			}
			case "deployed-files-production": {
				const d = (await axios.post("/api/admin/deployed-files/repair/production", null, CFG)).data ?? {};
				return deployedFilesOutcome(d, true);
			}
			case "search-health": {
				await startCoordinatorJob("/api/search/index/rebuild?target=all&language=fr", id);
				const state = await pollSearchRebuild();
				if (state === "timeout") {
					return {
						errorCount: 0,
						warningCount: 0,
						summary: "La reconstruction de l'index continue côté serveur : relancez l'analyse plus tard.",
						details: []
					};
				}
				return searchHealthOutcome();
			}
			default:
				throw new Error("Ce contrôle n'a pas de correction automatique.");
		}
	};

	/* ---- Handlers ------------------------------------------------------------------------------- */

	const runCheck = async (check: CheckDescriptorDto) => {
		const state = cards[check.id];
		if (state?.running || state?.fixing) return;
		updateCard(check.id, { running: true, error: null, notice: null });
		try {
			const outcome = await executeRun(check.id);
			updateCard(check.id, { running: false, outcome });
		} catch (err) {
			updateCard(check.id, { running: false, error: extractErrorMessage(err, "Échec de l'analyse.") });
		} finally {
			void loadHistoryFor(check.id);
		}
	};

	const fixCheck = async (check: CheckDescriptorDto) => {
		const state = cards[check.id];
		if (state?.running || state?.fixing) return;
		const confirmed = window.confirm(
			`Lancer la correction « ${check.label} » ?\n\n` +
				"La correction passe par les pipelines normaux de l'admin (re-clé, redéploiement, republication) : " +
				"aucune écriture directe dans les magasins de lecture."
		);
		if (!confirmed) return;
		updateCard(check.id, { fixing: true, error: null, notice: null });
		try {
			const outcome = await executeFix(check.id);
			updateCard(check.id, { fixing: false, outcome, notice: "Correction terminée." });
		} catch (err) {
			updateCard(check.id, { fixing: false, error: extractErrorMessage(err, "Échec de la correction.") });
		} finally {
			void loadHistoryFor(check.id);
		}
	};

	/* ---- Rendu ---------------------------------------------------------------------------------- */

	const renderCard = (check: CheckDescriptorDto) => {
		const state = cards[check.id] ?? EMPTY_CARD;
		const runs = history[check.id] ?? [];
		const last = runs[0];
		const outcome = state.outcome;
		return (
			<div
				key={check.id}
				data-testid={`check-card-${check.id}`}
				className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4"
			>
				<div className="flex flex-wrap items-start justify-between gap-2">
					<p className="text-sm font-semibold text-slate-800">{check.label}</p>
					<div className="flex shrink-0 flex-wrap gap-1">
						{(check.scopes ?? []).map((scope) => (
							<span
								key={scope}
								className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
							>
								{SCOPE_FR[scope] ?? scope}
							</span>
						))}
					</div>
				</div>

				{/* Tendance : errorCount des ~20 derniers runs historisés + date du dernier run. */}
				<div className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2">
					<div className="min-w-0 text-[11px] text-slate-500">
						{last ? (
							<>
								<p className="font-medium text-slate-600">Dernière analyse : {formatDateTimeFr(last.startedAtUtc)}</p>
								<p>
									{Number(last.errorCount ?? 0)} erreur(s) · {Number(last.warningCount ?? 0)} avertissement(s)
									{last.actor ? ` · ${last.actor}` : ""}
								</p>
							</>
						) : (
							<p>Aucune analyse enregistrée.</p>
						)}
					</div>
					<Sparkline checkId={check.id} runs={runs} />
				</div>

				{outcome && (
					<div className="space-y-2">
						<div className="flex flex-wrap items-center gap-2">
							<span
								className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
									outcome.errorCount > 0
										? "border-rose-200 bg-rose-50 text-rose-700"
										: "border-emerald-200 bg-emerald-50 text-emerald-700"
								}`}
							>
								{outcome.errorCount} erreur{outcome.errorCount > 1 ? "s" : ""}
							</span>
							<span
								className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
									outcome.warningCount > 0
										? "border-amber-200 bg-amber-50 text-amber-700"
										: "border-slate-200 bg-slate-50 text-slate-500"
								}`}
							>
								{outcome.warningCount} avertissement{outcome.warningCount > 1 ? "s" : ""}
							</span>
						</div>
						<p className="text-xs text-slate-600">{outcome.summary}</p>
						{outcome.details.length > 0 && (
							<details className="rounded-lg border border-slate-200 bg-slate-50">
								<summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-600">
									Détail ({outcome.details.length})
								</summary>
								<ul className="max-h-56 space-y-1 overflow-auto border-t border-slate-200 px-3 py-2">
									{outcome.details.map((line, index) => (
										<li key={index} className="break-all font-mono text-[11px] text-slate-600">
											{line}
										</li>
									))}
								</ul>
							</details>
						)}
					</div>
				)}

				{state.notice && <p className="text-xs text-slate-500">{state.notice}</p>}
				{state.error && (
					<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" role="alert">
						{state.error}
					</p>
				)}

				<div className="mt-auto flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={() => runCheck(check)}
						disabled={state.running || state.fixing}
						title={check.runEndpoint}
						className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{state.running ? <LoadingSpinner size="xs" inline /> : <FiSearch className="h-3.5 w-3.5" />}
						{state.running ? "Analyse en cours…" : "Analyser"}
					</button>
					{check.fixEndpoint ? (
						<GatedButton
							token={fixToken(check.id)}
							onClick={() => fixCheck(check)}
							disabled={state.running || state.fixing}
							title={check.fixEndpoint}
							className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{state.fixing ? <LoadingSpinner size="xs" inline /> : <FiTool className="h-3.5 w-3.5" />}
							{state.fixing ? "Correction en cours…" : "Corriger"}
						</GatedButton>
					) : null}
				</div>
			</div>
		);
	};

	return (
		<section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
			<div>
				<p className="text-base font-semibold text-slate-800">Analyse approfondie</p>
				<p className="max-w-3xl text-sm text-slate-600">
					Tous les contrôles de santé du site, pilotés par le catalogue du serveur : lancez chaque analyse à la
					demande, corrigez quand une correction automatique existe, et suivez la tendance des erreurs : chaque
					exécution est historisée automatiquement.
				</p>
			</div>

			{catalogError && (
				<div className="flex flex-wrap items-center gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
					<p className="text-sm text-rose-700" role="alert">
						{catalogError}
					</p>
					<button
						type="button"
						onClick={() => void loadCatalog()}
						className="inline-flex items-center rounded-lg border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
					>
						Réessayer
					</button>
				</div>
			)}

			{catalog === null && !catalogError && (
				<div className="flex items-center gap-2 text-sm text-slate-500">
					<LoadingSpinner size="sm" inline /> Chargement du catalogue des contrôles…
				</div>
			)}

			{catalog && catalog.length === 0 && <p className="text-sm text-slate-500">Aucun contrôle disponible.</p>}

			{catalog && catalog.length > 0 && <div className="grid gap-4 lg:grid-cols-2">{catalog.map(renderCard)}</div>}
		</section>
	);
};

export default DeepAnalyzePanel;
